import React, { useMemo } from 'react';
import GridItem from '../../../layout/GridComponents/GridItem';
import NoDataMessage from '../../../feedback/NoDataMessage.component';
import { FundInfoComponentProps } from '../../../layout/general/SimpleGeneralPageWrapper';
import { useNavReconShareClasses } from '../../../../api/rs-hg/nav-recon/hooks/queries';
import { QueryBoundary } from '../../../utilityComponents/QueryBoundary';
import CustomTable from '../../../tables/CustomTable';
import {
  NavReconShareClassTableRow,
  getNavReconShareClassesColumns,
} from './navReconShareClassesColumns';
import NavReconPositionsStats from '../positions/NavReconPositionsStats';

const TABLE_HEIGHT = 'calc(100vh - 15.62rem)';

/** Object payload so QueryBoundary does not treat an empty row list as missing data. */
type NavReconShareClassesLoaded = {
  rows: NavReconShareClassTableRow[];
  fundCurrency?: string;
};

/**
 * Fund-level NAV-Recon share class breakdown for the selected fund and date.
 */
const NavReconShareClassesPage: React.FC<FundInfoComponentProps> = ({
  positionDate,
  fundId,
}) => {
  const shareClassesQuery = useNavReconShareClasses(positionDate, fundId);

  const { rows, fundCurrency } = useMemo(() => {
    const payload = shareClassesQuery.data;
    if (!payload?.length) {
      return {
        rows: [] as NavReconShareClassTableRow[],
        fundCurrency: undefined as string | undefined,
      };
    }
    const block = payload.find((b) => b.fund_name === fundId) ?? payload[0];
    const list = block?.share_classes ?? [];
    return {
      fundCurrency: block?.fund_currency,
      rows: [...list]
        .sort((a, b) =>
          (a.share_class || '').localeCompare(b.share_class || ''),
        )
        .map((sc, i) => ({
          ...sc,
          index: i + 1,
          nav_t_minus_1: sc.nav_data?.nav_t_minus_1 ?? 0,
          nav_t: sc.nav_data?.nav_t ?? 0,
          reds: sc.nav_data?.reds ?? 0,
          subs: sc.nav_data?.subs ?? 0,
          nav_fx_t_minus_1: sc.nav_data?.fx_t_minus_1 ?? 0,
          nav_fx_t: sc.nav_data?.fx_t ?? 0,
          dividend: sc.price_data?.dividend ?? 0,
          price_fx_t_minus_1: sc.price_data?.fx_t_minus_1 ?? 0,
          price_fx_t: sc.price_data?.fx_t ?? 0,
          price_t_minus_1: sc.price_data?.price_t_minus_1 ?? 0,
          price_t: sc.price_data?.price_t ?? 0,
        })),
    };
  }, [shareClassesQuery.data, fundId]);

  const columns = useMemo(() => {
    const allColumns = getNavReconShareClassesColumns();
    const hasHedgedKey = rows.some(
      (row) => row.is_share_class_hedged !== undefined,
    );
    if (hasHedgedKey) return allColumns;
    return allColumns.filter((col) => col.field !== 'is_share_class_hedged');
  }, [rows]);

  const { csvFields, fieldsMap } = useMemo(() => {
    const exportable = columns.filter(
      (col) => col.field && col.field !== 'index',
    );
    return {
      csvFields: exportable.map((col) => col.field as string),
      fieldsMap: exportable
        .filter((col) => col.title)
        .map((col) => ({
          key: col.field as string,
          label: col.title as string,
        })),
    };
  }, [columns]);

  if (!fundId) {
    return (
      <GridItem card xs={12}>
        <NoDataMessage message="Select a fund to load share class data." />
      </GridItem>
    );
  }

  const loaded: NavReconShareClassesLoaded | undefined =
    shareClassesQuery.data ? { rows, fundCurrency } : undefined;

  return (
    <QueryBoundary<NavReconShareClassesLoaded>
      isLoading={shareClassesQuery.isLoading}
      isFetching={shareClassesQuery.isFetching}
      isError={shareClassesQuery.isError}
      error={shareClassesQuery.error}
      data={loaded}
      loadingMessages={['Loading share class data…']}
      errorMessage="Failed to load share classes."
      noDataMessage="No share class data for the selected fund and date."
    >
      {(data) => (
        <GridItem card xs={12}>
          <CustomTable<NavReconShareClassTableRow>
            csvFields={csvFields}
            fieldsMap={fieldsMap}
            showToolbar
            loadingHeight={TABLE_HEIGHT}
            toolbarComponents={{
              toolbarTitle: (
                <NavReconPositionsStats
                  title="Share classes"
                  fundCurrency={data.fundCurrency}
                />
              ),
              titleStyle: {
                fontWeight: 'inherit',
                fontSize: 'inherit',
                width: '100%',
              },
            }}
            columns={columns}
            data={data.rows}
            options={{
              paging: false,
              pageSize: Math.max(10, data.rows.length),
              pageSizeOptions: [10, 25, 50, data.rows.length || 10],
              search: true,
              exportButton: true,
              rowStyle: { padding: '0.62rem' },
              raptorExport: true,
            }}
          />
        </GridItem>
      )}
    </QueryBoundary>
  );
};

export default NavReconShareClassesPage;
